import { useEffect, useState } from "react";
import { Button, Checkbox, Pill, Textarea, useToast } from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { APPROVAL_LABEL } from "@astrum/shared";
import type { ApprovalDecision, ApprovalStatus, TaskApproval as TaskApprovalData } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import "./task-approval.css";

export const APPROVAL_TONE: Record<ApprovalStatus, PillTone> = {
  pending: "warn",
  approved: "ok",
  changes_requested: "info",
  rejected: "danger",
};

const DECISION_LABEL: Record<ApprovalDecision, string> = {
  approved: "Approved",
  changes_requested: "Changes requested",
  rejected: "Rejected",
};

type Member = { id: string; displayName: string };

interface Props {
  taskId: string;
  projectId: string | null;
  approval: TaskApprovalData | null;
  /** The viewer may edit the task, so may name its approvers. */
  canEdit: boolean;
  /** The task is done or cancelled: approvers can no longer be changed or decide. */
  closed: boolean;
  onChanged: (approval: TaskApprovalData | null) => void;
}

/**
 * Approval on a task: who must sign off, where each stands, and the buttons for
 * an approver to decide. Shown only for a task in a project, and only when
 * there is something to show or the viewer may start an approval.
 */
export function TaskApproval({ taskId, projectId, approval, canEdit, closed, onChanged }: Props) {
  const { toast } = useToast();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);

  const fail = (error: unknown, fallback: string) => toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" });

  useEffect(() => {
    if (!picking || !projectId) return;
    let cancelled = false;
    api
      .get<{ members: Member[] }>(`/projects/${projectId}`)
      .then((res) => !cancelled && setMembers(res.members))
      .catch((e: unknown) => !cancelled && fail(e, "Could not load the project's members."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking, projectId]);

  if (!projectId) return null;
  if (!approval && !(canEdit && !closed)) return null;

  const startPicking = () => {
    setChosen(approval?.approvers.map((a) => a.userId) ?? []);
    setPicking(true);
  };

  const saveApprovers = async () => {
    setBusy(true);
    try {
      const res = await api.put<{ approval: TaskApprovalData | null }>(`/tasks/${taskId}/approval`, { approverIds: chosen });
      onChanged(res.approval);
      setPicking(false);
    } catch (e) {
      fail(e, "Could not save the approvers.");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (decision: ApprovalDecision) => {
    setBusy(true);
    try {
      const res = await api.post<{ approval: TaskApprovalData | null }>(`/tasks/${taskId}/approval/decision`, {
        decision,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      onChanged(res.approval);
      setComment("");
    } catch (e) {
      fail(e, "Could not record your decision.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="task-detail__section task-approval">
      <div className="task-detail__section-header">
        <h3 className="task-detail__section-title">Approval</h3>
        {approval && <Pill tone={APPROVAL_TONE[approval.status]}>{APPROVAL_LABEL[approval.status]}</Pill>}
      </div>

      {approval && (
        <ul className="task-approval__list">
          {approval.approvers.map((a) => (
            <li key={a.userId} className="task-approval__row">
              <span className="task-approval__name">{a.displayName}</span>
              <span className="task-approval__decision">{a.decision ? DECISION_LABEL[a.decision] : "Waiting"}</span>
              {a.comment && <span className="task-approval__comment">{a.comment}</span>}
            </li>
          ))}
        </ul>
      )}
      {approval && approval.status !== "approved" && !closed && (
        <p className="task-approval__hint">This task cannot be marked done until every approver has approved.</p>
      )}

      {approval?.canDecide && (
        <div className="task-approval__decide">
          <Textarea label="Comment (required unless you approve)" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          <div className="task-approval__buttons">
            <Button size="sm" disabled={busy} onClick={() => void decide("approved")}>
              Approve
            </Button>
            <Button size="sm" variant="secondary" disabled={busy || !comment.trim()} onClick={() => void decide("changes_requested")}>
              Request changes
            </Button>
            <Button size="sm" variant="danger" disabled={busy || !comment.trim()} onClick={() => void decide("rejected")}>
              Reject
            </Button>
          </div>
        </div>
      )}

      {canEdit && !closed && !picking && (
        <div>
          <Button size="sm" variant="secondary" onClick={startPicking}>
            {approval ? "Change approvers" : "Request approval"}
          </Button>
        </div>
      )}
      {picking && (
        <div className="task-approval__picker">
          {members.length === 0 && <p className="task-approval__hint">Loading members…</p>}
          {members.map((m) => (
            <Checkbox
              key={m.id}
              label={m.displayName}
              checked={chosen.includes(m.id)}
              onChange={(e) => setChosen((prev) => (e.target.checked ? [...prev, m.id] : prev.filter((id) => id !== m.id)))}
            />
          ))}
          <div className="task-approval__buttons">
            <Button size="sm" disabled={busy} onClick={() => void saveApprovers()}>
              {chosen.length === 0 ? "Remove approval" : "Save approvers"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPicking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
